'use es6';

import { MODULE_TYPES, LEGACY_MODULE_TYPES, getModuleType, getLegacyModuleType } from 'EmailData/constants/DefaultModulesConstants';
import { VIDEO_TYPE_FIELD, HUBSPOT_VIDEO_FIELD, EMBED_FIELD, PLAYER_ID_FIELD, EMBED_URL_FIELD } from 'EmailEditorUI/constants/VideoConstants';
import { getSelectedSmartRuleBody, getIsImageModule, getIsVideoModule } from 'EmailEditorUI/utils/moduleUtils';
import { getDefaultForFieldFromModuleSpec } from './moduleUtils';
import { getImmutableOrPlain } from 'ContentEditorUI/utils/dataHelpers';
import { getIsBlogOrRssEmail } from '../redux/selectors/EmailSelectors';
const {
  IMAGE,
  EMAIL_VIDEO,
  RICH_TEXT,
  FOLLOW_ME,
  HTML,
  EMAIL_BODY,
  SOCIAL_SHARING,
  EMAIL_CAN_SPAM,
  LINKED_IMAGE,
  LOGO,
  EMAIL_LOGO,
  EMAIL_PRODUCTS,
  BUTTON,
  EMAIL_LINKED_IMAGE,
  EMAIL_SOCIAL_SHARING
} = MODULE_TYPES;
const {
  LOGO_LEGACY,
  LINKED_IMAGE_LEGACY,
  EMAIL_BODY_LEGACY,
  RICH_TEXT_LEGACY,
  EMAIL_RESUBSCRIBE_LEGACY,
  EMAIL_OPTIN_LEGACY,
  EMAIL_CAN_SPAM_LEGACY,
  SOCIAL_SHARING_LEGACY
} = LEGACY_MODULE_TYPES;

// default to the template schema module when a module value is not set
// this is required only in classic emails
const getModuleFieldOrDefault = (module, moduleSpec, fieldName) => {
  const moduleBody = getSelectedSmartRuleBody(module);
  if (!Array.isArray(fieldName)) {
    fieldName = [fieldName];
  }
  if (!moduleSpec || moduleBody && moduleBody.hasIn(fieldName)) {
    return moduleBody.getIn(fieldName);
  }
  return getDefaultForFieldFromModuleSpec(moduleSpec, fieldName[fieldName.length - 1]);
};
const PLACEHOLDER_RULES = {
  [RICH_TEXT]: (module, moduleSpec, state) => {
    const moduleType = getModuleType(module.get('module_id')) || getLegacyModuleType(module.get('type'));
    if ([EMAIL_BODY, EMAIL_BODY_LEGACY].includes(moduleType) && state && getIsBlogOrRssEmail(state)) {
      return false;
    }
    return !getModuleFieldOrDefault(module, moduleSpec, 'html');
  },
  [HTML]: (module, moduleSpec) => {
    return !getModuleFieldOrDefault(module, moduleSpec, 'html');
  },
  [FOLLOW_ME]: (module, moduleSpec) => {
    const socials = getModuleFieldOrDefault(module, moduleSpec, 'social');
    return !socials || !socials.size;
  },
  [IMAGE]: (module, moduleSpec) => {
    let src;
    if (module.get('widgetType') === 'linked_image') {
      src = getModuleFieldOrDefault(module, moduleSpec, 'src');
    } else {
      const img = getModuleFieldOrDefault(module, moduleSpec, 'img');
      src = img && getImmutableOrPlain(img, 'src');
    }
    return !src;
  },
  [LOGO]: (module, moduleSpec) => {
    const getLogoModuleFieldOrDefault = property => {
      return getModuleFieldOrDefault(module, moduleSpec, property) || getModuleFieldOrDefault(module, moduleSpec, ['img', property]);
    };
    const override = getLogoModuleFieldOrDefault('override_inherited_src');
    const src = getLogoModuleFieldOrDefault('src');
    const supressCompanyName = getLogoModuleFieldOrDefault('suppress_company_name');
    return override && !src && supressCompanyName;
  },
  [EMAIL_VIDEO]: module => {
    const moduleBody = getSelectedSmartRuleBody(module);
    if (!moduleBody) {
      return false;
    }
    if (moduleBody.get(VIDEO_TYPE_FIELD) === HUBSPOT_VIDEO_FIELD) {
      return !moduleBody.getIn([HUBSPOT_VIDEO_FIELD, PLAYER_ID_FIELD]);
    }
    return !moduleBody.get(EMBED_FIELD) || !moduleBody.getIn([EMBED_FIELD, EMBED_URL_FIELD]);
  },
  [SOCIAL_SHARING]: (module, moduleSpec) => {
    const social = [getModuleFieldOrDefault(module, moduleSpec, 'facebook'), getModuleFieldOrDefault(module, moduleSpec, 'linkedin'), getModuleFieldOrDefault(module, moduleSpec, 'pinterest'), getModuleFieldOrDefault(module, moduleSpec, 'twitter'), getModuleFieldOrDefault(module, moduleSpec, 'email')];
    const hasSocial = social.reduce((acc, next) => acc || next && getImmutableOrPlain(next, 'enabled'), false);
    return !hasSocial;
  },
  [EMAIL_PRODUCTS]: (module, moduleSpec, state, renderedOutput) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(renderedOutput.html, 'text/html');
    const tables = doc.querySelectorAll('.hs-ecomm-product');
    return tables.length === 0;
  },
  [BUTTON]: (module, moduleSpec, state, renderedOutput) => {
    if (!module || !module.has('body')) {
      return false;
    }
    const isLinkedToPayments = module.get('body').get('link_to') === 'payments';
    const parser = new DOMParser();
    const doc = parser.parseFromString(renderedOutput.html, 'text/html');
    const containsPayementsPlaceHolder = doc.querySelectorAll('.hs-payments-placeholder');
    return isLinkedToPayments && containsPayementsPlaceHolder.length > 0;
  }
};
export const getModuleTypeForPlaceholder = module => {
  const moduleType = getModuleType(module.get('module_id')) || getLegacyModuleType(module.get('type'));
  switch (moduleType) {
    case LINKED_IMAGE:
    case EMAIL_LINKED_IMAGE:
    case LINKED_IMAGE_LEGACY:
      {
        return IMAGE;
      }
    case LOGO:
    case LOGO_LEGACY:
    case EMAIL_LOGO:
      return LOGO;
    case EMAIL_SOCIAL_SHARING:
    case SOCIAL_SHARING:
    case SOCIAL_SHARING_LEGACY:
      {
        return SOCIAL_SHARING;
      }
    case EMAIL_BODY:
    case EMAIL_BODY_LEGACY:
    case RICH_TEXT:
    case RICH_TEXT_LEGACY:
    case EMAIL_RESUBSCRIBE_LEGACY:
    case EMAIL_OPTIN_LEGACY:
      {
        return RICH_TEXT;
      }
    case HTML:
    case EMAIL_CAN_SPAM:
    case EMAIL_CAN_SPAM_LEGACY:
      {
        return HTML;
      }
    default:
      return moduleType;
  }
};
export const getShouldUsePlaceholderForModule = ({
  module,
  moduleSpec = null,
  checkRenderedOutput = false,
  renderedOutput,
  state
}) => {
  if (checkRenderedOutput && (!renderedOutput || renderedOutput.shouldShowPlaceholder())) {
    return true;
  }
  const moduleType = getModuleTypeForPlaceholder(module);
  const placeholderRule = PLACEHOLDER_RULES[moduleType];
  if (placeholderRule) {
    return placeholderRule(module, moduleSpec, state, renderedOutput);
  }
  return false;
};
const createSpecificModulePlaceholderRule = moduleChecker => (module, moduleSpec) => {
  if (moduleChecker(module)) {
    return getShouldUsePlaceholderForModule({
      module,
      moduleSpec
    });
  }
  return false;
};
export const getIsEmptyImageModule = createSpecificModulePlaceholderRule(getIsImageModule);
export const getIsEmptyVideoModule = createSpecificModulePlaceholderRule(getIsVideoModule);